/**
 * A reorderable, multi-column grid of dashboard widgets. Order is per role
 * dashboard (`storageKey`) and persists via `useDashboardOrder`
 * (`lib/dashboardLayout.ts`) — DB-backed for non-admin staff, browser-local
 * for the admin account, same split as theme.
 *
 * The drag handle is a dedicated strip above each cell's content, not
 * overlaid on a corner of it — a `Widget`'s own header already has an icon,
 * a title, and sometimes an action link filling its full width, so
 * overlaying a handle on top of that would collide with real content for
 * some widgets and not others. A cell-level strip works the same way
 * regardless of what's inside (`Widget`, the bare `StatGrid`, or the
 * Diagnostics `<details>` block), and it's how the item becomes draggable —
 * dnd-kit's listeners are only ever attached to this handle, never to the
 * whole cell, so clicking a link or button inside a widget still works.
 */

import { useMemo } from 'react';
import type { ReactNode } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDashboardOrder } from '../lib/dashboardLayout.js';

export interface DashboardItem {
  id: string;
  span: 'full' | 'half';
  content: ReactNode;
}

function GripIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
      <circle cx="8" cy="6" r="1.6" fill="currentColor" />
      <circle cx="16" cy="6" r="1.6" fill="currentColor" />
      <circle cx="8" cy="12" r="1.6" fill="currentColor" />
      <circle cx="16" cy="12" r="1.6" fill="currentColor" />
      <circle cx="8" cy="18" r="1.6" fill="currentColor" />
      <circle cx="16" cy="18" r="1.6" fill="currentColor" />
    </svg>
  );
}

function SortableCell({ item }: { item: DashboardItem }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={
        'dashboard-grid__cell' +
        ` dashboard-grid__cell--${item.span}` +
        (isDragging ? ' dashboard-grid__cell--dragging' : '')
      }
    >
      <div className="dashboard-grid__handle-row">
        <button
          type="button"
          className="dashboard-grid__handle"
          aria-label="Drag to reorder, or focus and use the arrow keys"
          {...attributes}
          {...listeners}
        >
          <GripIcon />
        </button>
      </div>
      {item.content}
    </div>
  );
}

export function DashboardGrid({
  storageKey,
  items,
}: {
  storageKey: string;
  items: DashboardItem[];
}) {
  const knownIds = useMemo(() => items.map((item) => item.id), [items]);
  const { orderedIds, setOrder } = useDashboardOrder(storageKey, knownIds);

  const byId = useMemo(() => new Map(items.map((item) => [item.id, item])), [items]);
  const ordered = orderedIds
    .map((id) => byId.get(id))
    .filter((item): item is DashboardItem => item !== undefined);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event: DragEndEvent): void {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = orderedIds.indexOf(String(active.id));
    const newIndex = orderedIds.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    setOrder(arrayMove(orderedIds, oldIndex, newIndex));
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={orderedIds} strategy={rectSortingStrategy}>
        <div className="dashboard-grid">
          {ordered.map((item) => (
            <SortableCell key={item.id} item={item} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
